import { useQuery } from '@tanstack/react-query'

import { yearParam } from '@/features/clients/api/clientWire'
import {
  toCounterpartiesMoney,
  toCounterpartyStatement,
  type CounterpartiesMoneyWire,
  type CounterpartyStatementWire,
} from '@/features/counterparties/api/counterpartyMoneyWire'
import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_COUNTERPARTIES_MONEY, mockCounterpartyStatement } from '@/mocks/counterpartyMoney'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { CounterpartiesMoney, CounterpartyStatement, YearFilter } from '@/types/domain'

/* A counterparty's money (S-22e): the statement on its page and the figures on
 * the list. Composed at the root from casefile's works and finance's figures,
 * counted by the directory id only and bounded by the reader's case-money
 * reach — the server decides all of it, including what a reader may not see
 * (`moneyUnavailable`), and this client renders what it is sent.
 *
 * Both keys hang under `finance`, so a payment, a cost line, a leg's or a
 * unit's carrier change and a counterparty merge — each already invalidating
 * `finance.all` — refetch them without knowing they exist. */

/** One counterparty's statement for a year (`'all'` = every year). A 404
 *  covers a missing counterparty and a reader without `counterparty.read`
 *  alike; `enabled` is for a page that already knows the reader may not read. */
export function useCounterpartyStatement(
  id: string | undefined,
  year: YearFilter,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery<CounterpartyStatement>({
    queryKey: queryKeys.finance.counterpartyStatement(id ?? '', year),
    enabled: enabled && Boolean(id),
    queryFn: async () =>
      toCounterpartyStatement(
        USE_MOCKS
          ? mockCounterpartyStatement(id ?? '', year === 'all' ? null : year)
          : await api.get<CounterpartyStatementWire>(`/counterparties/${id}/statement`, {
              params: { year: yearParam(year) },
            }),
      ),
  })
}

/** Every counterparty's all-time figures over the reader's reach, one item per
 *  counterparty with a work. Never a 403: a reader who may not see them gets
 *  `moneyUnavailable: 'forbidden'` and no items. */
export function useCounterpartiesMoney({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<CounterpartiesMoney>({
    queryKey: queryKeys.finance.counterpartiesMoney(),
    enabled,
    queryFn: async () =>
      toCounterpartiesMoney(
        USE_MOCKS
          ? MOCK_COUNTERPARTIES_MONEY
          : await api.get<CounterpartiesMoneyWire>('/counterparties/money'),
      ),
  })
}
